import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ApiService } from './api.service';
import { UserStateService } from '../state/user-state.service';
import { NotificationService } from '../notification.service';
import { IPageRequestDTO } from '@models/paging';

import {
  StudentDTO,
  IStudentUserDTO,
  IStudentListItemDTO,
  IStudentInviteRequestDTO,
  IUpdateStudentRequestDTO,
  IClassroomStudentDTO,
  IStudentDTO,
} from '@models/student';

import { IPagedResults } from '@app/infrastructure/models/response';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class ClassroomStudentService {
  private baseUrl: string;
  private url: string;

  constructor(
    private apiService: ApiService,
    private userStateService: UserStateService,
    private notificationService: NotificationService,
  ) {
    this.userStateService.getUserSession().subscribe((user) => {
      if (user && user.teacher?.id) {
        this.baseUrl = `${environment.apiRoute}/teachers/${user.teacher?.id}/classrooms`;
      }
    });
  }

  private buildUrl(classroomId: string): string {
    const controllerRoute = `${classroomId}/students`;
    return `${this.baseUrl}/${controllerRoute}`;
  }

  public getStudentListByGradingPeriodId(
    gradingPeriodId: string,
    classroomId: string,
    pageRequest: IPageRequestDTO,
  ): Observable<IStudentListItemDTO[]> {
    const endpoint = this.buildUrl(classroomId);
    let params = new HttpParams();
    if (pageRequest.pageIndex) {
      params = params.set('page', pageRequest.pageIndex.toString());
    }
    if (pageRequest.pageSize) {
      params = params.set('pageSize', pageRequest.pageSize.toString());
    }
    if (gradingPeriodId) {
      params = params.set('gradingPeriodId', gradingPeriodId);
    }

    return this.apiService.get<IStudentListItemDTO[]>(endpoint, {
      params,
    });
  }

  public getStudentList(
    classroomId: string,
    pageRequest: IPageRequestDTO,
  ): Observable<IStudentListItemDTO[]> {
    const endpoint = this.buildUrl(classroomId);
    let params = new HttpParams();
    if (pageRequest.pageIndex) {
      params = params.set('page', pageRequest.pageIndex.toString());
    }
    if (pageRequest.pageSize) {
      params = params.set('pageSize', pageRequest.pageSize.toString());
    }

    return this.apiService.get<IStudentListItemDTO[]>(endpoint, {
      params,
    });
  }

  public getStudentById(
    classroomId: string,
    studentId: string,
  ): Observable<IStudentDTO> {
    const endpoint = `${this.buildUrl(classroomId)}/${studentId}`;
    return this.apiService.get<IStudentDTO>(endpoint);
  }

  public updateStudent(
    classroomId: string,
    studentId: string,
    payload: IUpdateStudentRequestDTO,
  ): Observable<IClassroomStudentDTO> {
    const endpoint = `${this.buildUrl(classroomId)}/${studentId}`;
    return this.apiService.patch<
      IClassroomStudentDTO,
      IUpdateStudentRequestDTO
    >(endpoint, payload);
  }

  // TODO: Not sure of what the return type is supposed to be, right now the server doesn't give us anything back so left it as any
  public sendInviteCode(
    classroomId: string,
    payload: IStudentInviteRequestDTO[],
  ): Observable<any> {
    const endpoint = this.buildUrl(classroomId);

    return this.apiService.post<any, IStudentInviteRequestDTO[]>(
      endpoint,
      payload,
    );
  }

  public removeStudent(
    classroomId: string,
    studentId: string,
  ): Observable<IClassroomStudentDTO> {
    const endpoint = `${this.buildUrl(classroomId)}/${studentId}`;

    return this.apiService.delete<IClassroomStudentDTO>(endpoint).pipe(
      tap(() => {
        const message = `Student removed from classroom.`;
        return this.notificationService.showSuccess([message]);
      }),
    );
  }
}
